import { useLogById } from '@/api/hooks/useIntegrationLogs'
import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import { useOrderDashboardIssues } from '@/api/hooks/useOrderDashboard'
import { LogDetailsSidebar } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsSidebar'
import type { DashboardWindow } from '@/pages/Dashboards/dateRange'
import { useTenantStore } from '@/stores/tenantStore'
import type { EnvMode } from '@/utils/envMode'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Alert, Card } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { type MetricFilters, filterIssuesByMetrics } from '../metricFilters'
import { IssueCountCards } from './IssueCountCards'
import { IssueList } from './IssueList'
import { IssueScopeToggle } from './IssueScopeToggle'
import { MetricFilterTags } from './MetricFilterTags'
import {
  type IssueSelection,
  countIssues,
  filterIssuesByBucket,
} from './issueCounts'
import { type IssueScope, filterIssuesByScope, issueKey } from './issueFields'
import type { IssueGroup } from './issueGroups'

interface IssuesSectionProps {
  reportWindow: DashboardWindow
  /** Test/Prod, or undefined for every instance. Matches the metrics report's
   * so both panes of the dashboard describe the same set of orders. */
  mode: EnvMode | undefined
  /** Partner and event picked in the metric grids beside this section. */
  metricFilters: MetricFilters
  onChangeMetricFilters: (filters: MetricFilters) => void
  /** Severity and issue type, held with the rest of the page's filters. */
  selection: IssueSelection
  onChangeSelection: (selection: IssueSelection) => void
}

/**
 * Issue listing and resolution.
 *
 * One request per window, everything else narrowed here: the endpoint can
 * filter by ownership, severity and category, but the toggle needs all three
 * ownership counts at once and the cards need every severity at once, so
 * asking the backend would mean a request per number on screen.
 */
export function IssuesSection({
  reportWindow,
  mode,
  metricFilters,
  onChangeMetricFilters,
  selection,
  onChangeSelection,
}: IssuesSectionProps) {
  const [scope, setScope] = useState<IssueScope>('all')
  // The card picked, and the failure of its issue that the panel shows.
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null)
  const [openIssue, setOpenIssue] = useState<OrderDashboardIssue | null>(null)
  const viewGroup = (group: IssueGroup) => {
    setOpenGroupKey(group.key)
    setOpenIssue(group.latest)
  }
  const closeIssue = () => {
    setOpenGroupKey(null)
    setOpenIssue(null)
  }
  const { selectedTenant } = useTenantStore()
  const tenantId = selectedTenant?.id ?? null
  const tenantName = selectedTenant?.name ?? null

  const { data, error, isLoading, refetch } = useOrderDashboardIssues(
    reportWindow,
    mode,
  )

  // Ahead of the scope toggle so its own counts are of the same set the list
  // is drawn from -- a partner filter should narrow the toggle's numbers too.
  const onMetrics = useMemo(
    () => filterIssuesByMetrics(data, metricFilters),
    [data, metricFilters],
  )
  const scoped = useMemo(
    () => filterIssuesByScope(onMetrics, scope, tenantId),
    [onMetrics, scope, tenantId],
  )
  const counts = useMemo(() => countIssues(scoped), [scoped])
  const listed = useMemo(
    () => filterIssuesByBucket(scoped, selection.bucket, selection.category),
    [scoped, selection],
  )

  const scopeCounts = useMemo(
    () => ({
      all: data ? countIssues(onMetrics).issues.total : undefined,
      mine: data
        ? countIssues(filterIssuesByScope(onMetrics, 'mine', tenantId)).issues
            .total
        : undefined,
      partner: data
        ? countIssues(filterIssuesByScope(onMetrics, 'partner', tenantId))
            .issues.total
        : undefined,
    }),
    [data, onMetrics, tenantId],
  )

  // The retry endpoint waits for the saga to finish before it answers, so
  // there is nothing left to wait for by the time a retry resolves.
  const refetchIssues = () => refetch()

  // A retried log stops being an open issue, so the panel would otherwise sit
  // there showing something no longer in the list. Keyed off the unfiltered
  // response, not the visible rows -- narrowing the filters should leave an
  // open issue open.
  useEffect(() => {
    if (!openIssue || !data) return
    const open = issueKey(openIssue)
    if (!data.some((issue) => issueKey(issue) === open)) closeIssue()
  }, [data, openIssue])

  const openGroupId = openIssue?.log_group_id ?? null
  // The sidebar wants the whole log group for context (routing, order,
  // sibling logs); an issue on its own only carries the group's id.
  const { data: logGroup } = useLogById(openGroupId, tenantName ?? undefined)

  return (
    // The details panel is a sibling of the whole section, not just the list,
    // so opening it narrows the filters above too rather than sliding in over
    // a layout that stays put.
    <Horizontal gap={8} style={{ flex: 1, minHeight: 0 }}>
      <Vertical gap={12} flex={1.5} style={{ minHeight: 0 }}>
        {error && (
          <Alert
            type='error'
            showIcon
            message='Could not load order issues'
            description={String(error)}
          />
        )}
        <Horizontal
          gap={8}
          justifyContent='space-between'
          alignItems='center'
          style={{ flexWrap: 'wrap' }}
        >
          <IssueScopeToggle
            value={scope}
            onChange={setScope}
            counts={scopeCounts}
          />
          <MetricFilterTags
            filters={metricFilters}
            onChange={onChangeMetricFilters}
          />
        </Horizontal>
        <IssueCountCards
          counts={counts}
          selection={selection}
          onSelect={onChangeSelection}
        />
        <IssueList
          issues={listed}
          loading={isLoading}
          tenantId={tenantId}
          tenantName={tenantName}
          selectedKey={openGroupKey}
          onViewIssue={viewGroup}
          onRetried={refetchIssues}
        />
      </Vertical>

      {/* Only mounted with an issue picked, so the section takes its full
          width back when the panel is closed -- and so the card chrome does
          not sit there as an empty box. The sidebar carries its own flex
          weight for the logs pages; here the card holds it instead. */}
      {openIssue && (
        <Card
          size='small'
          className='issue-details-card'
          style={{
            flex: 0.75,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          styles={{
            body: { padding: 0, flex: 1, minHeight: 0, display: 'flex' },
          }}
        >
          <LogDetailsSidebar
            selectedLog={openIssue}
            groupMetadata={logGroup?.metadata ?? null}
            selectedTenantName={tenantName}
            logGroupId={openGroupId}
            refetchLogs={refetchIssues}
            onClose={closeIssue}
          />
        </Card>
      )}
    </Horizontal>
  )
}
